import {
  useRef, useEffect, useCallback,
} from 'react'

/**
 * Hook to scroll to bottom on initial message load, resetting when session changes.
 *
 * @param containerRef - Ref to the scrollable container
 * @param messagesLength - Current number of messages (triggers scroll when > 0)
 * @param sessionId - Session identifier (resets scroll tracking when it changes)
 * @param isStreaming - Whether currently streaming (skips scroll if messages from streaming)
 */
// eslint-disable-next-line import/prefer-default-export
export const useInitialScrollToBottom = (
  containerRef: React.RefObject<HTMLDivElement>,
  messagesLength: number,
  sessionId?: string,
  isStreaming?: boolean,
): void => {
  const hasScrolled = useRef(false)
  // Track if streaming was ever active - if so, messages came from streaming, not history
  const wasStreamingRef = useRef(false)

  const scrollToBottom = useCallback(() => {
    if (containerRef.current) {
      // eslint-disable-next-line no-param-reassign
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }
  }, [containerRef])

  // Track when streaming becomes active - messages from streaming should not trigger initial scroll
  useEffect(() => {
    if (isStreaming) {
      wasStreamingRef.current = true
    }
  }, [isStreaming])

  // Scroll to bottom on initial message load (from history only, not from streaming)
  useEffect(() => {
    // Skip if messages came from streaming (not loading history)
    if (wasStreamingRef.current) {
      return
    }
    if (messagesLength > 0 && !hasScrolled.current) {
      requestAnimationFrame(() => {
        scrollToBottom()
        hasScrolled.current = true
      })
    }
  }, [messagesLength, scrollToBottom])

  // Reset when session changes, but NOT if we're mid-stream
  // (Session ID is assigned mid-stream for new conversations, so we must not reset wasStreamingRef)
  useEffect(() => {
    if (!isStreaming) {
      hasScrolled.current = false
      wasStreamingRef.current = false
    }
  }, [sessionId, isStreaming])
}
